import { Node } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { v4 } from 'uuid';
import { EmbedResourceComponent } from './PreviewComponent/EmbedResourceComponent.js';
import { EMBED_RESOURCE_TYPE_NAME } from './contants.js';
import { isNumber } from '@fabric-app/utils/guards';
import { EmbedResourceDropHandlerKey, createPasteDropPlugin } from './PasteDropPlugin.js';
import { handleEmbedUpload } from './handleUpload.js';

const embedV2Version = "0.0.3";
const EmbedV2 = Node.create({
  name: EMBED_RESOURCE_TYPE_NAME,
  /**
  * do not change this, updated from block to inline due to notion sync data structure
  * if reverted, things will start do disappear
  */
  group: "inline",
  inline: true,
  selectable: true,
  atom: true,
  draggable: true,
  addNodeView() {
    return ReactNodeViewRenderer(EmbedResourceComponent, {
      stopEvent: ({ event }) => {
        if (/drag|drop|mousedown/.test(event.type) && event.target instanceof HTMLElement && !!event.target.closest("[draggable=false]")) {
          event.preventDefault();
          event.stopPropagation();
          return true;
        }
        return false;
      }
    });
  },
  addAttributes() {
    return {
      alt: {
        default: null
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-title"),
        renderHTML: (attributes) => ({
          "data-title": attributes["data-title"]
        })
      },
      width: {
        default: null
      },
      height: {
        default: null
      },
      ["data-uuid"]: {
        default: v4(),
        parseHTML: (element) => element.getAttribute("data-uuid"),
        renderHTML: (attributes) => ({
          "data-uuid": attributes["data-uuid"]
        })
      },
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-src"),
        renderHTML: (attributes) => ({
          "data-src": attributes.src
        })
      },
      extension: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-extension"),
        renderHTML: (attributes) => ({
          "data-extension": attributes.extension
        })
      },
      mimeType: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("data-mime-type");
        },
        renderHTML: (attributes) => {
          return {
            "data-mime-type": attributes.mimeType
          };
        }
      },
      loading: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-loading"),
        renderHTML: (attributes) => ({
          "data-loading": attributes.loading
        })
      },
      kind: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("data-kind");
        },
        renderHTML: (attributes) => {
          return {
            "data-kind": attributes.kind
          };
        }
      },
      resourceId: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("data-resource-id");
        },
        renderHTML: (attributes) => {
          return {
            "data-resource-id": attributes.resourceId
          };
        }
      },
      uploadProgress: {
        default: null,
        parseHTML: (element) => {
          const val = Number(element.getAttribute("data-upload-progress"));
          return isNumber(val) ? val : null;
        },
        renderHTML: (attributes) => ({
          "data-upload-progress": attributes.uploadProgress
        })
      },
      parentResourceId: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("data-parent-resource-id");
        },
        renderHTML: (attributes) => {
          return {
            "data-parent-resource-id": attributes.parentResourceId
          };
        }
      },
      resourceKey: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute("data-resource-key");
        },
        renderHTML: (attributes) => {
          return {
            "data-resource-key": attributes.resourceKey
          };
        }
      },
      version: {
        default: embedV2Version,
        parseHTML: (element) => {
          const elementVersion = element.getAttribute("data-version") || embedV2Version;
          return elementVersion;
        },
        renderHTML: (attributes) => {
          return {
            "data-version": attributes.version
          };
        }
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: 'img[data-node-type="embedV2"],div[data-node-type="embedV2"], span[data-node-type="embedV2"]'
      }
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    const isLoading = HTMLAttributes["data-loading"] === "true";
    if (isLoading) {
      return [
        "span",
        {
          ["data-node-type"]: EMBED_RESOURCE_TYPE_NAME,
          ["data-loading"]: true
        }
      ];
    }
    const dataMimeType = HTMLAttributes["data-mime-type"];
    if (dataMimeType?.startsWith("image/")) {
      return [
        "img",
        {
          ...HTMLAttributes,
          src: HTMLAttributes["data-src"],
          ["data-node-type"]: EMBED_RESOURCE_TYPE_NAME
        }
      ];
    }
    return [
      "span",
      {
        ...HTMLAttributes,
        ["data-node-type"]: EMBED_RESOURCE_TYPE_NAME
      },
      [
        node.attrs.title,
        node.attrs.extension
      ].filter(Boolean).join(".")
    ];
  },
  addProseMirrorPlugins() {
    const { onUpload } = this.storage;
    return [
      createPasteDropPlugin({
        onUpload
      })
    ];
  },
  addCommands() {
    return {
      setUploadHandler: (onUpload) => {
        return ({ editor }) => {
          const transaction = editor.state.tr.setMeta(EmbedResourceDropHandlerKey, {
            onUpload
          });
          editor.view.dispatch(transaction);
          return true;
        };
      },
      setCheckBeforeUploadHandler: (onCheckBeforeUpload) => {
        return ({ editor }) => {
          const transaction = editor.state.tr.setMeta(EmbedResourceDropHandlerKey, {
            onCheckBeforeUpload
          });
          editor.view.dispatch(transaction);
          return true;
        };
      },
      uploadResource: (file) => ({ editor, tr }) => {
        const pos = editor.state.selection.from;
        const view = editor.view;
        const onCheckBeforeUpload = EmbedResourceDropHandlerKey.getState(view.state)?.onCheckBeforeUpload;
        if (onCheckBeforeUpload && onCheckBeforeUpload({
          files: [
            file
          ]
        }) === "ABORT") {
          return false;
        }
        const onUpload = EmbedResourceDropHandlerKey.getState(view.state)?.onUpload;
        if (onUpload) {
          handleEmbedUpload({
            onUpload,
            file,
            view,
            pos
          });
          tr.setMeta("preventUpdate", true);
          return true;
        }
        return false;
      }
    };
  }
});

export { EmbedV2, embedV2Version };
//# sourceMappingURL=EmbedExtension.js.map
